import { Html } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { useRef } from 'react';
import type { Graph3DPositionedNode } from '../core/graph.types';

interface NodeDetailPanel3DProps {
  node: Graph3DPositionedNode;
  detail: string;
  onKeepOpen: () => void;
  onScheduleClose: () => void;
}

/**
 * 通用的节点悬浮详情面板：任何节点类型只要 runtime.nodes[id].detail 有内容就会
 * 显示，组件库本身不理解 detail 的业务含义（由业务方填充）。
 */
// 节点默认 renderer（ProcessNode3D 等）里的文字 <Html> 未显式传 zIndexRange，使用
// drei 的默认值 [16777271, 0]；这个面板必须始终盖在节点文字之上，所以这里用一个远高于
// 默认上限、且区间极窄（近似常量）的 zIndexRange，使计算出的 z-index 基本不受相机距离
// 影响，稳定压过任何默认 Html 内容。
const DETAIL_PANEL_Z_INDEX_RANGE: [number, number] = [20000001, 20000000];

const DETAIL_PANEL_GAP = 14;
const DETAIL_PANEL_EDGE_MARGIN = 16;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export function NodeDetailPanel3D({
  node,
  detail,
  onKeepOpen,
  onScheduleClose,
}: NodeDetailPanel3DProps) {
  const { gl } = useThree();
  const contentRef = useRef<HTMLDivElement>(null);
  const appliedOffsetRef = useRef({
    x: DETAIL_PANEL_GAP,
    y: -DETAIL_PANEL_GAP,
  });

  // 面板默认锚点在节点右上方（向右下方展开）。这个场景支持 OrbitControls 自由旋转/平移/
  // 缩放，节点投影到屏幕上的位置会随时变化，因此每一帧都要重新测量面板尺寸与画布边界，
  // 若默认展开方向会超出画布右侧/下侧就翻转到节点另一侧展开，翻转后仍放不下（面板本身比
  // 画布还大）则再夹到画布范围内，保证面板始终整体可见。用 ref 直接写 DOM 而不是 React
  // state，避免每帧都触发组件重渡染。
  useFrame(() => {
    const el = contentRef.current;
    if (!el) {
      return;
    }
    const rect = el.getBoundingClientRect();
    const containerRect = gl.domElement.getBoundingClientRect();
    const { x: appliedX, y: appliedY } = appliedOffsetRef.current;
    const nodeX = rect.left - appliedX;
    const nodeY = rect.top - appliedY;
    const width = rect.width;
    const height = rect.height;

    const preferLeft =
      nodeX + DETAIL_PANEL_GAP + width >
      containerRect.right - DETAIL_PANEL_EDGE_MARGIN;
    const desiredLeft = preferLeft
      ? nodeX - DETAIL_PANEL_GAP - width
      : nodeX + DETAIL_PANEL_GAP;
    const minLeft = containerRect.left + DETAIL_PANEL_EDGE_MARGIN;
    const maxLeft = Math.max(
      minLeft,
      containerRect.right - DETAIL_PANEL_EDGE_MARGIN - width
    );
    const clampedLeft = clamp(desiredLeft, minLeft, maxLeft);

    const preferUp =
      nodeY - DETAIL_PANEL_GAP + height >
      containerRect.bottom - DETAIL_PANEL_EDGE_MARGIN;
    const desiredTop = preferUp
      ? nodeY - DETAIL_PANEL_GAP - height
      : nodeY - DETAIL_PANEL_GAP;
    const minTop = containerRect.top + DETAIL_PANEL_EDGE_MARGIN;
    const maxTop = Math.max(
      minTop,
      containerRect.bottom - DETAIL_PANEL_EDGE_MARGIN - height
    );
    const clampedTop = clamp(desiredTop, minTop, maxTop);

    const nextOffset = { x: clampedLeft - nodeX, y: clampedTop - nodeY };
    if (
      Math.abs(appliedX - nextOffset.x) >= 0.5 ||
      Math.abs(appliedY - nextOffset.y) >= 0.5
    ) {
      appliedOffsetRef.current = nextOffset;
      el.style.transform = `translate(${nextOffset.x}px, ${nextOffset.y}px)`;
    }
  });

  return (
    <Html
      position={[
        node.resolvedPosition.x,
        node.resolvedPosition.y,
        node.resolvedPosition.z,
      ]}
      zIndexRange={DETAIL_PANEL_Z_INDEX_RANGE}
      style={{ pointerEvents: 'none' }}
    >
      <div
        ref={contentRef}
        style={{
          transform: `translate(${appliedOffsetRef.current.x}px, ${appliedOffsetRef.current.y}px)`,
          pointerEvents: 'auto',
          width: 348,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: '70vh',
          overflowY: 'auto',
          boxSizing: 'border-box',
          borderRadius: 6,
          border: '1px solid #e2e8f0',
          background: '#ffffff',
          padding: 12,
          fontSize: 11,
          lineHeight: '1.6',
          color: '#334155',
          boxShadow:
            '0 10px 25px -5px rgba(0,0,0,.2), 0 8px 10px -6px rgba(0,0,0,.1)',
          whiteSpace: 'pre-wrap',
        }}
        onMouseEnter={onKeepOpen}
        onMouseLeave={onScheduleClose}
        onWheel={event => event.stopPropagation()}
        onPointerDown={event => event.stopPropagation()}
        onMouseDown={event => event.stopPropagation()}
      >
        {detail}
      </div>
    </Html>
  );
}
